import { Security } from "@okta/okta-react";
import React, { useEffect } from "react";
import HttpInterceptor from "./api/HttpInterceptor";
import { oktaAuth, UserProvider } from "./contexts/UserContext";
import { DiamondVideoPlayerProvider } from "./contexts/DiamondVideoPlayerContext";
import { AlertProvider, AlertsFromProvider, BCR, LoadingProvider, useAlerts } from "best-common-react";
import useGameReviewStore from "./store/GameReviewStore";
import { QueryClientProvider } from "@tanstack/react-query";
import queryClient from "./api/mlbauth/QueryClient";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PostMessageAction, ErrorPostMessage } from "./types/Eyeball";
import { MlbAuthColors, MlbAuthTheme } from "./theme/MlbAuthTheme";
import { createTheme, ThemeProvider } from "@mui/material";

type WrapperProviderProps = {
  children: React.ReactNode;
};

const WrapperProviderInner = ({ children }: WrapperProviderProps) => {
  const { addAlert } = useAlerts();

  useEffect(() => {
    const messageHandler = (event: MessageEvent) => {
      if (event.origin !== window.MLBBest.envVariables.EYEBALL_APP_ORIGIN) return;

      const { setRefreshReview, setEyeballSyncing } = useGameReviewStore.getState();

      switch (event.data.action) {
        case PostMessageAction.REFRESH:
          setRefreshReview(true);
          break;
        case PostMessageAction.SYNCING:
          setEyeballSyncing(true);
          break;
        case PostMessageAction.ERROR:
          const errorData = event.data as ErrorPostMessage;
          const alertType = errorData.errorType === "conflict" ? "warning" : "danger";
          addAlert({
            type: alertType,
            text: errorData.errorMessage,
            id: `sync-error-${Date.now()}`,
          });
          break;
        case PostMessageAction.EDIT_STARTED:
          const editStartedData = event.data as { action: string; editItemId: number };
          useGameReviewStore.setState({
            editMode: true,
            editItemId: editStartedData.editItemId,
          });
          break;
      }
    };

    window.addEventListener("message", messageHandler);

    return () => window.removeEventListener("message", messageHandler);
  }, [addAlert]);

  return (
    <>
      <AlertsFromProvider />
      <UserProvider>
        <DiamondVideoPlayerProvider>
          <LoadingProvider>
            <HttpInterceptor>{children}</HttpInterceptor>
            <ReactQueryDevtools initialIsOpen={false} />
          </LoadingProvider>
        </DiamondVideoPlayerProvider>
      </UserProvider>
    </>
  );
};

const WrapperProvider = ({ children }: WrapperProviderProps) => {
  const theme = createTheme({
    palette: {
      primary: {
        light: MlbAuthColors.blue,
        main: MlbAuthColors.blue,
        dark: MlbAuthColors.blue,
      },
    },
    typography: {
      fontFamily: "var(--bs-font-sans-serif)",
      button: {
        textTransform: "none",
        fontWeight: 600,
      },
    },
  });

  const restoreOriginalUri = async (_oktaAuth: any, originalUri: string): Promise<void> => {
    window.location.replace(originalUri);
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <BCR theme={MlbAuthTheme}>
            <AlertProvider>
              <WrapperProviderInner>{children}</WrapperProviderInner>
            </AlertProvider>
          </BCR>
        </ThemeProvider>
      </QueryClientProvider>
    </Security>
  );
};

export default WrapperProvider;
